import { Card, Flex, IconButton } from '@brightdrop/bd-ui';
import { Divider, ListItemText, Menu, MenuItem, useTheme } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { type MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetOrgEnrollmentQuery } from '~/api/generated-api';
import DotsHorizontalIcon from '~/assets/icons/dotsHorizontal.svg?react';
import TranslatedLabel, {
  translateText,
} from '~/common/components/TranslatedLabel/TranslatedLabel';
import { useOrgId } from '~/common/hooks/useOrgId';
import OverviewFieldList from '~/common/overviewDetails/OverviewFieldList';
import PartnerLogo from '~/features/integrations/partners/PartnerLogo';
import type { ManageSectionProps } from '~/features/integrations/partners/types';

import useStyles from './AccountDetailsCard.styles';
import useOverviewCardStyles from './overview-card.styles';

const TRANSLATION_KEYS = {
  title: 'integrations:account.manage.accountDetails',
  actions: 'integrations:account.manage.accountActions',
  managePartner: 'integrations:account.manage.managePartner',
  unlinkAccount: 'integrations:account.manage.unlinkAccount',
} as const;

const ACTIONS_MENU_ID = 'account-integrations-account-actions-menu';

/**
 * Account details for a linked partner. Field rows come from that partner's manage config.
 */
const AccountDetailsCard = ({ partner }: ManageSectionProps): JSX.Element => {
  const { classes, cx } = useStyles();
  const { classes: cardClasses } = useOverviewCardStyles();
  const theme = useTheme();
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);
  const isActionsOpen = Boolean(actionsAnchor);
  // Labels resolve during render, so subscribe or they go stale on a locale change.
  useTranslation(['integrations', 'common']);
  const organizationsId = useOrgId();
  const { manage, providerCode, id, nameKey } = partner;
  const partnerName = translateText(nameKey);
  const closeActions = () => setActionsAnchor(null);
  const { currentData, isError } = useGetOrgEnrollmentQuery(
    organizationsId
      ? { 'cs-organization': organizationsId, providerCode }
      : skipToken
  );
  // A rejected read leaves the previous row on the cache entry, and a
  // `success: false` body is still stored when the HTTP status was 200.
  const enrollment =
    !isError && currentData?.success ? currentData.result : undefined;

  return (
    <Card dataTestId="account-integrations-account-details">
      <Flex className={cx(classes.aligned, classes.header)}>
        <Flex className={classes.aligned}>
          <TranslatedLabel
            translationKey={TRANSLATION_KEYS.title}
            typographyVariant="header2_medium"
            className={cardClasses.title}
            component="h2"
          />
        </Flex>
        <Flex className={classes.aligned}>
          <PartnerLogo
            partnerId={id}
            variant="wordmark"
            className={classes.logo}
            aria-hidden
          />
          <IconButton
            icon={<DotsHorizontalIcon />}
            aria-label={translateText(TRANSLATION_KEYS.actions)}
            aria-haspopup="menu"
            aria-expanded={isActionsOpen}
            aria-controls={isActionsOpen ? ACTIONS_MENU_ID : undefined}
            data-testid="account-integrations-account-actions"
            onClick={(event: MouseEvent<HTMLButtonElement>) =>
              setActionsAnchor(event.currentTarget)
            }
          />
          <Menu
            id={ACTIONS_MENU_ID}
            anchorEl={actionsAnchor}
            open={isActionsOpen}
            onClose={closeActions}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            data-testid="account-integrations-account-actions-menu"
          >
            <MenuItem
              onClick={closeActions}
              data-testid="account-integrations-manage-partner"
              sx={{ width: '220px' }}
            >
              <ListItemText
                primary={
                  <TranslatedLabel
                    translationKey={TRANSLATION_KEYS.managePartner}
                    typographyVariant="body_regular"
                    variables={{ partner: partnerName }}
                  />
                }
              />
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={closeActions}
              data-testid="account-integrations-unlink-account"
              sx={{ width: '220px' }}
            >
              <ListItemText
                primary={
                  <TranslatedLabel
                    translationKey={TRANSLATION_KEYS.unlinkAccount}
                    typographyVariant="body_regular"
                    style={{ color: theme.new.color.errorPrimary }}
                  />
                }
              />
            </MenuItem>
          </Menu>
        </Flex>
      </Flex>
      <OverviewFieldList
        fields={manage.accountFields(enrollment)}
        testId="partner-account"
        paddedRows
      />
    </Card>
  );
};

export default Object.assign(AccountDetailsCard, {
  sectionId: 'account-details',
});
